// hooks/use-following-feed.ts
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface FeedItem {
  id: string;
  title: string;
  description: string;
  image_urls: string[];
  location: string;
  created_at: string;
  user_id: string;
  type: "listing" | "post";
  sellerUsername?: string;
  sellerAvatarUrl?: string;
  sellerIsFollowing?: boolean;
  price?: string;
  currency?: string | null;
  condition?: string;
  grade?: string;
  quantity?: number;
  initialSaved?: boolean;
  likesCount?: number;
  initialLiked?: boolean;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  auction_start_price?: number | null;
  auction_highest_bid?: number | null;
  auction_end_at?: string | null;
  auction_status?: string | null;
  sellerSalesCount?: number;
  card_details?: {
    tcg_name?: string | null;
    set_name?: string | null;
    rarity_name?: string | null;
    condition_name?: string | null;
    grading_company_name?: string | null;
    grade_value?: number | null;
    grade_label?: string | null;
    badges?: string[];
    defects?: string[];
  } | null;
}

interface FollowingFeedResponse {
  items: FeedItem[];
  hasNextPage: boolean;
  page: number;
  totalCount: number;
}

const DEFAULT_LIMIT = 15;

interface FetchOptions {
  replace?: boolean;
  preservePageState?: boolean;
}

export function useFollowingFeed(userId?: string, limit: number = DEFAULT_LIMIT) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadMoreLockRef = useRef(false);

  const fetchPage = useCallback(
    async (targetPage: number, options: FetchOptions = {}) => {
      if (!userId) {
        return;
      }

      const { replace = false, preservePageState = false } = options;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: targetPage.toString(),
          limit: limit.toString(),
          userId,
        });

        const response = await fetch(`/api/feed/following?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data: FollowingFeedResponse = await response.json();
        if (controller.signal.aborted) {
          return;
        }

        setItems((prev) => {
          if (replace) {
            const seen = new Set<string>();
            const next: FeedItem[] = [];

            for (const entry of data.items) {
              const key = `${entry.type}-${entry.id}`;
              seen.add(key);
              next.push(entry);
            }

            for (const entry of prev) {
              const key = `${entry.type}-${entry.id}`;
              if (!seen.has(key)) {
                next.push(entry);
              }
            }

            return next;
          }

          const indexById = new Map<string, number>();
          const next = prev.map((entry, idx) => {
            indexById.set(`${entry.type}-${entry.id}`, idx);
            return entry;
          });

          for (const entry of data.items) {
            const key = `${entry.type}-${entry.id}`;
            const existingIndex = indexById.get(key);
            if (existingIndex !== undefined) {
              next[existingIndex] = { ...next[existingIndex], ...entry };
            } else {
              indexById.set(key, next.length);
              next.push(entry);
            }
          }

          return next;
        });

        if (!preservePageState) {
          setPage(data.page);
        }
        setHasNextPage(data.hasNextPage);
        setIsInitialized(true);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [limit, userId]
  );

  const loadMore = useCallback(() => {
    if (loadMoreLockRef.current || isLoading || !hasNextPage || !userId) {
      return;
    }
    loadMoreLockRef.current = true;
    fetchPage(page + 1).finally(() => {
      loadMoreLockRef.current = false;
    });
  }, [fetchPage, hasNextPage, isLoading, page, userId]);

  const loadFirstPage = useCallback(() => {
    if (!userId) {
      return;
    }
    fetchPage(1, { replace: true });
  }, [fetchPage, userId]);

  const refetchFirstPage = useCallback(() => {
    if (!userId) {
      return;
    }
    fetchPage(1, { replace: true, preservePageState: true });
  }, [fetchPage, userId]);

  const refresh = refetchFirstPage;

  useEffect(() => {
    setItems([]);
    setPage(0);
    setHasNextPage(true);
    setIsInitialized(false);
    loadFirstPage();
  }, [loadFirstPage, userId]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    items,
    page,
    hasNextPage,
    isLoading,
    isInitialized,
    error,
    loadMore,
    refresh,
    refetchFirstPage,
  };
}
